import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useBlocker, useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRtmGuidelines } from '@/hooks/rtm/useRtmGuidelines';
import { useAccount } from '@/hooks/useAccount';
import useBrains from '@/hooks/useBrains';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';
import useDialogs from '@/hooks/useDialogs';
import { useDirtyFormBlocker } from '@/hooks/useDirtyFormBlocker';
import { useEffectiveTools } from '@/hooks/useEffectiveTools';
import useMembers from '@/hooks/useMembers';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import type { ChannelGuidelines } from '@/shared/types/brain';
import { PageName } from '@/shared/types/segment';
import { GUIDELINES_DOCS_URL } from '@/util/constants';
import { timeFromNowShort } from '@/util/dateUtils';

import { CharactersLimit } from '../components/CharactersLimit';
import {
  ChannelGuidelinesEditor,
  ChannelGuidelinesEditorHandle,
} from '../components/Editors/ChannelGuidelinesEditor';
import { CustomInstructionsEditor } from '../components/Editors/CustomInstructionsEditor';
import { GuidelineEditorHeader } from '../components/Editors/GuidelineEditorHeader';
import { ObjectionsGuidelineEditor } from '../components/Editors/ObjectionsGuidelineEditor';
import { SystemGuidelineEditor } from '../components/Editors/SystemGuidelineEditor';
import { GuidelinesHeader } from '../components/Guidelines/GuidelinesHeader';
import {
  StickyNavigation,
  StickyNavigationSection,
} from '../components/Guidelines/StickyNavigation';
import { UnsavedChangesAlert } from '../components/Guidelines/UnsavedChangesAlert/UnsavedChangesAlert';
import { HEADING_BLOCK_SELECTOR } from '../helper';
import { useGuidelinesCharacterCount } from '../hooks/useGuidelinesCharacterCount';
import { GuidelinesCharacterLimitModal } from '../modals/GuidelinesCharacterLimitModal';
import { CharacterCountsByField, GuidelineCharacterField } from '../models';
// pt-6 (24px) positions the tabs row that the ToC and the rail are tuned against.
// They only line up with the `about_guidelines` banner dismissed — it sits above.
const CONTAINER_CLASS = 'guidelines-editor mx-auto min-h-full w-full max-w-200 mb-96 px-4 pt-6';

type EditorHandlers = {
  onEditorReady: (editor: { getMarkdown: () => Promise<string> }) => void;
  onChange: () => void;
};

const ContentSkeleton = () => {
  return (
    <div className={CONTAINER_CLASS}>
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-3/4" />
      </div>

      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/5" />
      </div>

      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
};

const GuidelinesPageEnhanced = ({ brainId }: { brainId: string }) => {
  const { t } = useTranslation();
  const { account } = useAccount();

  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const { brain, patchBrainGuidelinesAsync, patchBrainGuidelinesStatus, getStatus } =
    useBrains(brainId);
  const { getMember } = useMembers();

  useRtmGuidelines(brainId);
  const { dialogs } = useDialogs(brainId);

  const lastUpdatedUser = getMember(brain?.guidelines?.updated_by);

  const { effectiveTools: enabledTools } = useEffectiveTools(brain);

  const onNavigateToLocalTools = useCallback(() => {
    if (slug && brainId) {
      navigate(`/${slug}/ai-agents/${brainId}/mcps?tab=local`);
    }
  }, [slug, brainId, navigate]);

  const isCustomerSupport = brain?.brain_type === 'support';
  const isDebtCollection = brain?.brain_type === 'debt_collection';
  const isSeller = !isCustomerSupport && !isDebtCollection;

  useTitle(t('pages.ai_agent_guidelines', { 0: brain?.name ?? '-' }));

  // Track live character counts for each field
  const [liveFieldLengths, setLiveFieldLengths] = useState<CharacterCountsByField>({});
  // Track live markdown content for each field (used by StickyNavigation for real-time heading updates)
  const [liveMarkdownByField, setLiveMarkdownByField] = useState<Record<string, string>>({});

  const { remainingCharacters, countsByField, totalCharacters, isSoftWarning } =
    useGuidelinesCharacterCount({
      characterLimit: account?.max_guidelines_characters,
      liveFieldLengths,
    });
  const [isCharacterLimitModalOpen, setCharacterLimitModalOpen] = useState(false);

  // Track which editors have unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Track header visibility for sticky alert
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  // Track initial markdown values to compare against
  const initialMarkdownRef = useRef<{
    goal?: string;
    overview?: string;
    features?: string;
    loyalty?: string;
    objections?: string;
    custom_instructions?: string;
  }>({});
  // Track if editors are initialized (to avoid false positives during initialization)
  const editorsInitializedRef = useRef<{
    goal?: boolean;
    overview?: boolean;
    features?: boolean;
    loyalty?: boolean;
    objections?: boolean;
    custom_instructions?: boolean;
  }>({});

  // Store editor refs for each field
  const editorRefs = useRef<{
    goal?: { getMarkdown: () => Promise<string> };
    overview?: { getMarkdown: () => Promise<string> };
    features?: { getMarkdown: () => Promise<string> };
    loyalty?: { getMarkdown: () => Promise<string> };
    objections?: { getMarkdown: () => Promise<string> };
    custom_instructions?: { getMarkdown: () => Promise<string> };
  }>({});

  // Channel guidelines editor ref
  const channelGuidelinesRef = useRef<ChannelGuidelinesEditorHandle | null>(null);
  const channelGuidelinesInitializedRef = useRef(false);

  // Helper function to create consistent onEditorReady handlers
  const createEditorReadyHandler = useCallback(
    (fieldName: GuidelineCharacterField) =>
      async (editor: { getMarkdown: () => Promise<string> }) => {
        editorRefs.current[fieldName] = editor;
        // Baseline must come from the editor's own serializer, not the raw API
        // string, so markdown round-trip drift isn't mistaken for a user edit.
        // A field left uninitialized is dropped from dirty detection for good.
        initialMarkdownRef.current[fieldName] = await editor.getMarkdown().catch((error) => {
          console.error('Failed to capture guidelines baseline:', error);
          return '';
        });
        setTimeout(() => {
          editorsInitializedRef.current[fieldName] = true;
        }, 100);
      },
    []
  );

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!brainId) {
      return false;
    }

    try {
      const guidelines: Partial<Record<string, string>> = {};

      // Collect content from all editors
      if (editorRefs.current.goal) {
        guidelines.goal = await editorRefs.current.goal.getMarkdown();
      }
      if (editorRefs.current.overview) {
        guidelines.overview = await editorRefs.current.overview.getMarkdown();
      }
      if (editorRefs.current.features) {
        guidelines.features = await editorRefs.current.features.getMarkdown();
      }
      if (editorRefs.current.loyalty) {
        guidelines.loyalty = await editorRefs.current.loyalty.getMarkdown();
      }
      if (editorRefs.current.objections) {
        guidelines.objections = await editorRefs.current.objections.getMarkdown();
      }
      if (editorRefs.current.custom_instructions) {
        guidelines.custom_instructions = await editorRefs.current.custom_instructions.getMarkdown();
      }

      // Collect channel guidelines only when they differ from the baseline.
      // Otherwise an unchanged save would PATCH `channel_guidelines: {}`, which
      // the backend would treat as "clear all channel guidelines".
      let channelGuidelines: ChannelGuidelines | undefined;
      if (channelGuidelinesRef.current && (await channelGuidelinesRef.current.hasChanges())) {
        channelGuidelines = await channelGuidelinesRef.current.getMarkdown();
      }

      // Save using patchBrainGuidelines
      await patchBrainGuidelinesAsync({
        brain_id: brainId,
        ...guidelines,
        ...(channelGuidelines !== undefined && { channel_guidelines: channelGuidelines }),
      });

      // Update initial markdown values to reflect saved state
      if (editorRefs.current.goal) {
        initialMarkdownRef.current.goal = await editorRefs.current.goal.getMarkdown();
      }
      if (editorRefs.current.overview) {
        initialMarkdownRef.current.overview = await editorRefs.current.overview.getMarkdown();
      }
      if (editorRefs.current.features) {
        initialMarkdownRef.current.features = await editorRefs.current.features.getMarkdown();
      }
      if (editorRefs.current.loyalty) {
        initialMarkdownRef.current.loyalty = await editorRefs.current.loyalty.getMarkdown();
      }
      if (editorRefs.current.objections) {
        initialMarkdownRef.current.objections = await editorRefs.current.objections.getMarkdown();
      }
      if (editorRefs.current.custom_instructions) {
        initialMarkdownRef.current.custom_instructions =
          await editorRefs.current.custom_instructions.getMarkdown();
      }

      // Re-snapshot channel guidelines baseline to the saved content
      if (channelGuidelinesRef.current) {
        await channelGuidelinesRef.current.resetBaseline();
      }

      // Clear unsaved changes flags after successful save
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      console.error('Failed to save guidelines:', error);
      return false;
    }
  }, [brainId, patchBrainGuidelinesAsync]);

  // No baseline seeding here: `createEditorReadyHandler` overwrites every field
  // from the editor's serializer first, so seeding only races it.
  useEffect(() => {
    if (!brain) {
      return;
    }

    setHasUnsavedChanges(false);
    setLiveFieldLengths({});
    setLiveMarkdownByField({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brain?.brain_id]);

  // Check if any editor has unsaved changes by comparing current content with initial content
  const checkUnsavedChanges = useCallback(async () => {
    if (!brainId) {
      return false;
    }

    try {
      const checks = await Promise.all([
        editorRefs.current.goal && editorsInitializedRef.current.goal
          ? editorRefs.current.goal.getMarkdown().then((current) => {
              const initial = initialMarkdownRef.current.goal || '';
              return current.trim() !== initial.trim();
            })
          : Promise.resolve(false),
        editorRefs.current.overview && editorsInitializedRef.current.overview
          ? editorRefs.current.overview.getMarkdown().then((current) => {
              const initial = initialMarkdownRef.current.overview || '';
              return current.trim() !== initial.trim();
            })
          : Promise.resolve(false),
        editorRefs.current.features && editorsInitializedRef.current.features
          ? editorRefs.current.features.getMarkdown().then((current) => {
              const initial = initialMarkdownRef.current.features || '';
              return current.trim() !== initial.trim();
            })
          : Promise.resolve(false),
        editorRefs.current.loyalty && editorsInitializedRef.current.loyalty
          ? editorRefs.current.loyalty.getMarkdown().then((current) => {
              const initial = initialMarkdownRef.current.loyalty || '';
              return current.trim() !== initial.trim();
            })
          : Promise.resolve(false),
        editorRefs.current.objections && editorsInitializedRef.current.objections
          ? editorRefs.current.objections.getMarkdown().then((current) => {
              const initial = initialMarkdownRef.current.objections || '';
              return current.trim() !== initial.trim();
            })
          : Promise.resolve(false),
        editorRefs.current.custom_instructions && editorsInitializedRef.current.custom_instructions
          ? editorRefs.current.custom_instructions.getMarkdown().then((current) => {
              const initial = initialMarkdownRef.current.custom_instructions || '';
              return current.trim() !== initial.trim();
            })
          : Promise.resolve(false),
      ]);

      if (checks.some((hasChanges) => hasChanges)) {
        return true;
      }

      // Check channel guidelines for changes
      if (channelGuidelinesInitializedRef.current && channelGuidelinesRef.current) {
        return channelGuidelinesRef.current.hasChanges();
      }

      return false;
    } catch (error) {
      console.error('Failed to check unsaved changes:', error);
      return false;
    }
  }, [brainId]);

  const refreshUnsavedChanges = useCallback(() => {
    checkUnsavedChanges().then((hasChanges) => {
      setHasUnsavedChanges(hasChanges);
    });
  }, [checkUnsavedChanges]);

  // Dirty immediately, undirty on a pause: a debounce alone never settles during
  // continuous typing, leaving the badge and the navigation guard disarmed.
  const markDirty = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const scheduleUnsavedChangesCheck = useDebouncedCallback(refreshUnsavedChanges, 300, [
    refreshUnsavedChanges,
  ]);

  // Handler for editor changes - only check after editor is initialized
  const handleEditorChange = useCallback(
    (field: GuidelineCharacterField) => {
      // Only check if editor is initialized to avoid false positives during initialization
      if (!editorsInitializedRef.current[field]) {
        return;
      }

      // Get live character count and markdown from the editor
      const editor = editorRefs.current[field];
      if (editor) {
        editor.getMarkdown().then((markdown) => {
          const charCount = Array.from(markdown).length;
          setLiveFieldLengths((prev) => ({ ...prev, [field]: charCount }));
          setLiveMarkdownByField((prev) => ({ ...prev, [field]: markdown }));
        });
      }

      markDirty();
      scheduleUnsavedChangesCheck();
    },
    [markDirty, scheduleUnsavedChangesCheck]
  );

  // BlockNote resubscribes whenever `onChange` changes identity, so keep it stable.
  const editorHandlers = useMemo<Record<GuidelineCharacterField, EditorHandlers>>(() => {
    const handlersFor = (field: GuidelineCharacterField): EditorHandlers => ({
      onEditorReady: createEditorReadyHandler(field),
      onChange: () => handleEditorChange(field),
    });

    return {
      goal: handlersFor('goal'),
      overview: handlersFor('overview'),
      features: handlersFor('features'),
      loyalty: handlersFor('loyalty'),
      objections: handlersFor('objections'),
      custom_instructions: handlersFor('custom_instructions'),
    };
  }, [createEditorReadyHandler, handleEditorChange]);

  const handleChannelGuidelinesReady = useCallback(
    (handle: ChannelGuidelinesEditorHandle) => {
      channelGuidelinesRef.current = handle;
      channelGuidelinesInitializedRef.current = true;
      refreshUnsavedChanges();
    },
    [refreshUnsavedChanges]
  );

  const handleChannelGuidelinesChange = useCallback(() => {
    if (!channelGuidelinesInitializedRef.current) {
      return;
    }
    markDirty();
    scheduleUnsavedChangesCheck();
  }, [markDirty, scheduleUnsavedChangesCheck]);

  // Blocker for character limit
  const characterLimitBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    const isNavigatingAway = currentLocation.pathname !== nextLocation.pathname;
    return remainingCharacters < 0 && isNavigatingAway;
  });

  // Mirror local unsaved-changes state into Redux so the shared blocker sees it.
  useTrackFormState({ isDirty: hasUnsavedChanges, formId: 'guidelines' });

  // Blocker for unsaved changes: shared modal, saves on leave and stays open on failure.
  const unsavedChangesBlocker = useDirtyFormBlocker(async () => {
    const saved = await handleSave();
    if (!saved) {
      throw new Error('Failed to save guidelines');
    }
  });

  useEffect(() => {
    // Prompt the user when navigation is blocked due to character overflows.
    if (characterLimitBlocker.state === 'blocked') {
      setCharacterLimitModalOpen(true);
    }
    // Close the modal once navigation is allowed again.
    if (characterLimitBlocker.state === 'unblocked') {
      setCharacterLimitModalOpen(false);
    }
  }, [characterLimitBlocker.state]);

  useEffect(() => {
    // Automatically dismiss the modal when the draft returns within the limit.
    if (remainingCharacters >= 0 && isCharacterLimitModalOpen) {
      setCharacterLimitModalOpen(false);
    }
  }, [remainingCharacters, isCharacterLimitModalOpen]);

  // Track header visibility with Intersection Observer for sticky alert
  useEffect(() => {
    // Only set up observer when brain exists (header is rendered)
    if (!brain) {
      return;
    }

    const headerElement = headerRef.current;
    if (!headerElement) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderVisible(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(headerElement);

    return () => {
      observer.disconnect();
    };
  }, [brain]);

  const handleContinueEditing = useCallback(() => {
    // Keep the user on the page to continue editing, while clearing the blocker.
    setCharacterLimitModalOpen(false);
    characterLimitBlocker.reset?.();
  }, [characterLimitBlocker]);

  const handleDiscardAndLeave = useCallback(() => {
    // Let the user leave, acknowledging that over-limit changes will be discarded.
    setCharacterLimitModalOpen(false);
    characterLimitBlocker.proceed?.();
  }, [characterLimitBlocker]);

  // Keep the latest field counts available synchronously so concurrent saves see fresh totals.
  const characterSnapshotRef = useRef<{ counts: CharacterCountsByField; total: number }>({
    counts: countsByField,
    total: totalCharacters,
  });

  characterSnapshotRef.current = { counts: countsByField, total: totalCharacters };

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  // Cached per id so React stops re-attaching every section ref on every render.
  const sectionRefCallbacks = useRef<Record<string, (node: HTMLElement | null) => void>>({});

  const registerSectionRef = useCallback((id: string) => {
    if (!sectionRefCallbacks.current[id]) {
      sectionRefCallbacks.current[id] = (node: HTMLElement | null) => {
        if (node) {
          sectionRefs.current[id] = node;
        } else {
          delete sectionRefs.current[id];
        }
      };
    }

    return sectionRefCallbacks.current[id];
  }, []);

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [activeHeading, setActiveHeading] = useState<{
    sectionId: string;
    headingIndex: number;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'guidelines' | 'channel-specific'>('guidelines');

  const isCharacterLimitExceeded = remainingCharacters < 0;

  const handleSaveClick = useCallback(() => {
    if (isCharacterLimitExceeded) {
      return;
    }
    handleSave();
  }, [isCharacterLimitExceeded, handleSave]);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as 'guidelines' | 'channel-specific');
  }, []);

  const navigateToSection = useCallback((sectionId: string) => {
    const node = sectionRefs.current?.[sectionId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const guidelineSections = useMemo<StickyNavigationSection[]>(() => {
    const sections: StickyNavigationSection[] = [];

    if (!isCustomerSupport) {
      sections.push({ id: 'goal', label: t('common.goal') });
    }

    if (isSeller) {
      sections.push(
        { id: 'overview', label: t('ai_agents.knowledge.overview.title') },
        { id: 'features', label: t('ai_agents.knowledge.features.title') },
        { id: 'loyalty', label: t('ai_agents.knowledge.loyalty.title') }
      );
    }

    sections.push(
      { id: 'objections', label: t('ai_agents.knowledge.objections.title') },
      { id: 'custom_instructions', label: t('ai_agents.knowledge.custom_instructions.title') }
    );

    return sections;
  }, [isCustomerSupport, isSeller, t]);

  const channelGuidelineSections = useMemo<StickyNavigationSection[]>(
    () => [
      { id: 'voice', label: t('ai_agents.channel_guidelines.voice') },
      { id: 'email', label: t('ai_agents.channel_guidelines.email') },
      { id: 'chat', label: t('ai_agents.channel_guidelines.chat') },
    ],
    [t]
  );

  const activeSections =
    activeTab === 'channel-specific' ? channelGuidelineSections : guidelineSections;

  const handleChannelChange = useCallback(
    (channel: 'voice' | 'email' | 'chat', markdown: string) => {
      setLiveFieldLengths((prev) => ({ ...prev, [channel]: Array.from(markdown).length }));
      setLiveMarkdownByField((prev) => ({ ...prev, [channel]: markdown }));
    },
    []
  );

  // Scroll-spy: track which section is currently in view
  useEffect(() => {
    const refs = sectionRefs.current;
    if (!refs || Object.keys(refs).length === 0) {
      return;
    }

    const visibleSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = Object.entries(refs).find(([, el]) => el === entry.target)?.[0];
          if (!sectionId) {
            return;
          }

          if (entry.isIntersecting) {
            visibleSections.add(sectionId);
          } else {
            visibleSections.delete(sectionId);
          }
        });

        if (visibleSections.size === 0) {
          return;
        }

        // Among visible sections, find the one closest to the top of the viewport
        let bestId: string | null = null;
        let bestTop = Infinity;
        visibleSections.forEach((id) => {
          const el = refs[id];
          if (!el) {
            return;
          }
          const top = el.getBoundingClientRect().top;
          if (top < bestTop) {
            bestTop = top;
            bestId = id;
          }
        });

        if (bestId) {
          setActiveSectionId(bestId);
        }
      },
      { rootMargin: '-80px 0px -50% 0px', threshold: 0 }
    );

    Object.values(refs).forEach((el) => {
      if (el) {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [guidelineSections]);

  // Heading-level scroll spy: track which heading is currently in view
  useEffect(() => {
    const container = document.querySelector('.guidelines-editor');
    if (!container) {
      return;
    }

    const visibleHeadings = new Set<Element>();

    const headingObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleHeadings.add(entry.target);
          } else {
            visibleHeadings.delete(entry.target);
          }
        });

        if (visibleHeadings.size === 0) {
          return;
        }

        let bestEl: Element | null = null;
        let bestTop = Infinity;
        visibleHeadings.forEach((el) => {
          const top = el.getBoundingClientRect().top;
          if (top < bestTop) {
            bestTop = top;
            bestEl = el;
          }
        });

        if (!bestEl) {
          setActiveHeading(null);
          return;
        }

        const sectionEntry = Object.entries(sectionRefs.current).find(([, el]) =>
          el?.contains(bestEl)
        );
        if (!sectionEntry?.[1]) {
          setActiveHeading(null);
          return;
        }

        const [sectionId, sectionEl] = sectionEntry;

        // Filter to only H1/H2 with text to match extractHeadingsFromMarkdown
        const relevantHeadings = Array.from(
          sectionEl.querySelectorAll(HEADING_BLOCK_SELECTOR)
        ).filter((el) => el.querySelector('h1, h2') && el.textContent?.trim());
        const headingIndex = relevantHeadings.indexOf(bestEl);

        if (headingIndex >= 0) {
          setActiveHeading({ sectionId, headingIndex });
        }
      },
      { rootMargin: '-80px 0px -50% 0px', threshold: 0 }
    );

    const observeHeadings = () => {
      const headings = container.querySelectorAll(HEADING_BLOCK_SELECTOR);
      headings.forEach((h) => headingObserver.observe(h));
    };

    // Observe existing headings
    observeHeadings();

    // Watch for new headings added dynamically by BlockNote editors
    let debounceTimer: ReturnType<typeof setTimeout>;
    let lastHeadingCount = container.querySelectorAll(HEADING_BLOCK_SELECTOR).length;

    const mutationObserver = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const currentCount = container.querySelectorAll(HEADING_BLOCK_SELECTOR).length;
        if (currentCount !== lastHeadingCount) {
          lastHeadingCount = currentCount;
          headingObserver.disconnect();
          visibleHeadings.clear();
          observeHeadings();
        }
      }, 300);
    });

    mutationObserver.observe(container, { childList: true, subtree: true });

    return () => {
      clearTimeout(debounceTimer);
      headingObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [brain?.brain_id]);

  if (!brain) {
    return <ContentSkeleton />;
  }

  const userFallback = lastUpdatedUser?.name ? lastUpdatedUser.name.charAt(0).toUpperCase() : '?';

  // Make the editor inert while a dialog is open so its drag handle can't be
  // summoned on hover behind the overlay (BlockNote ignores pointer-events). (MOV-16242)
  const isLeaveModalOpen = unsavedChangesBlocker.state === 'blocked' || isCharacterLimitModalOpen;

  return (
    <>
      {/* Horizontal padding mirrors PageSubHeader so the ToC aligns with the breadcrumb across breakpoints */}
      <div className="grid min-h-full w-full grid-cols-1 content-between items-start gap-6 px-4 lg:px-5 xl:grid-cols-[17rem_minmax(0,1fr)_17rem] xl:px-6 2xl:px-8">
        {/* Left column: ToC + Last updated, sticky together inside the main column */}
        {/* 16px + the label's centring in its min-h-12 row = the tabs row */}
        <div className="sticky top-0 hidden h-[calc(100vh-6.5rem)] flex-col gap-4 self-start py-4 xl:flex">
          <StickyNavigation
            inline
            // -ml-2 nudges the ToC label left toward the breadcrumb (optical, not flush)
            className="-ml-2 min-h-0 flex-1"
            sections={activeSections}
            onSelect={navigateToSection}
            liveMarkdownByField={liveMarkdownByField}
            activeSectionId={activeSectionId}
            activeHeading={activeHeading}
          />

          {lastUpdatedUser && (
            <div className="flex flex-col gap-1 pl-4">
              <div className="flex gap-1 text-xs leading-4 font-medium text-muted-foreground">
                <span>{t('common.last_updated')}</span>
                {getStatus === 'pending' ? (
                  <Skeleton className="h-4 w-20" />
                ) : (
                  <span className="font-semibold">
                    {timeFromNowShort(brain.guidelines?.updated_at) ?? t('common.not_available')}
                  </span>
                )}
              </div>
              <div className="flex min-h-6 items-center gap-2">
                <Avatar size="xxs">
                  <AvatarImage
                    src={lastUpdatedUser?.avatar || ''}
                    alt={`${lastUpdatedUser?.name}'s avatar`}
                  />
                  <AvatarFallback>{userFallback}</AvatarFallback>
                </Avatar>
                <span className="line-clamp-2 max-w-[136px] text-sm leading-none font-medium text-foreground">
                  {lastUpdatedUser?.name ?? t('common.unknown_user')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Middle column: editor + sticky save alert */}
        <div className={CONTAINER_CLASS} inert={isLeaveModalOpen}>
          <ContextualHelp
            name="about_guidelines"
            title={t('ai_agents.guidelines.contextual_help.title')}
            links={[{ label: t('docs.docs'), url: GUIDELINES_DOCS_URL }]}
          >
            {t('ai_agents.guidelines.contextual_help.description')}
          </ContextualHelp>

          <div className="mb-6 xl:hidden">
            <GuidelinesHeader
              className="flex-row flex-wrap justify-between gap-6"
              helpTooltipSide="top"
            />
          </div>

          {isSoftWarning && <CharactersLimit remainingCharacters={remainingCharacters} />}

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div ref={headerRef} className="flex items-end justify-between gap-6">
              <div className="flex-1 pb-2">
                <TabsList variant="line" className="w-full">
                  <TabsTrigger variant="line" value="guidelines">
                    {t('ai_agents.channel_guidelines.tab_guidelines')}
                  </TabsTrigger>
                  <TabsTrigger variant="line" value="channel-specific">
                    {t('ai_agents.channel_guidelines.tab_channel_specific')}
                  </TabsTrigger>
                </TabsList>
              </div>

              <InfoTooltip
                open={isCharacterLimitExceeded || hasUnsavedChanges ? undefined : false}
                content={
                  isCharacterLimitExceeded
                    ? t('ai_agents.saving_status.unable_to_save_changes')
                    : t('ai_agents.saving_status.there_are_unsaved_changes')
                }
              >
                <div className="relative pb-2">
                  <Button type="button" size="sm" onClick={handleSaveClick}>
                    {t('common.save')}
                  </Button>

                  {hasUnsavedChanges && !isCharacterLimitExceeded && (
                    <Badge
                      type="bordered"
                      flavour="destructive"
                      size="md"
                      className="absolute -top-1.5 -right-1.5 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)]"
                      aria-label={t('ai_agents.saving_status.there_are_unsaved_changes')}
                    />
                  )}
                </div>
              </InfoTooltip>
            </div>

            <TabsContent
              value="guidelines"
              forceMount
              hidden={activeTab !== 'guidelines'}
              className="mt-6 px-4"
            >
              {/* Hide Goal for Customer Support AI Agents */}
              {!isCustomerSupport && (
                <section
                  id="guideline-goal"
                  ref={registerSectionRef('goal')}
                  className="scroll-mt-28"
                >
                  <SystemGuidelineEditor
                    fieldToSave="goal"
                    title={<GuidelineEditorHeader title={t('common.goal')} />}
                    {...editorHandlers.goal}
                    enabledTools={enabledTools}
                    dialogs={dialogs}
                    onNavigateToLocalTools={onNavigateToLocalTools}
                  />
                  <Separator decorative className="my-6 text-border" />
                </section>
              )}

              {isSeller && (
                <>
                  <section
                    id="guideline-overview"
                    ref={registerSectionRef('overview')}
                    className="scroll-mt-28"
                  >
                    <SystemGuidelineEditor
                      fieldToSave="overview"
                      title={
                        <GuidelineEditorHeader title={t('ai_agents.knowledge.overview.title')} />
                      }
                      {...editorHandlers.overview}
                      enabledTools={enabledTools}
                      dialogs={dialogs}
                      onNavigateToLocalTools={onNavigateToLocalTools}
                    />
                    <Separator decorative className="my-6 text-border" />
                  </section>

                  <section
                    id="guideline-features"
                    ref={registerSectionRef('features')}
                    className="scroll-mt-28"
                  >
                    <SystemGuidelineEditor
                      fieldToSave="features"
                      title={
                        <GuidelineEditorHeader title={t('ai_agents.knowledge.features.title')} />
                      }
                      {...editorHandlers.features}
                      enabledTools={enabledTools}
                      dialogs={dialogs}
                      onNavigateToLocalTools={onNavigateToLocalTools}
                    />
                    <Separator decorative className="my-6 text-border" />
                  </section>

                  <section
                    id="guideline-loyalty"
                    ref={registerSectionRef('loyalty')}
                    className="scroll-mt-28"
                  >
                    <SystemGuidelineEditor
                      fieldToSave="loyalty"
                      title={
                        <GuidelineEditorHeader title={t('ai_agents.knowledge.loyalty.title')} />
                      }
                      {...editorHandlers.loyalty}
                      enabledTools={enabledTools}
                      dialogs={dialogs}
                      onNavigateToLocalTools={onNavigateToLocalTools}
                    />
                    <Separator decorative className="my-6 text-border" />
                  </section>
                </>
              )}

              <section
                id="guideline-objections"
                ref={registerSectionRef('objections')}
                className="scroll-mt-28"
              >
                <ObjectionsGuidelineEditor
                  {...editorHandlers.objections}
                  enabledTools={enabledTools}
                  dialogs={dialogs}
                  onNavigateToLocalTools={onNavigateToLocalTools}
                />
              </section>

              <section
                id="guideline-custom-instructions"
                ref={registerSectionRef('custom_instructions')}
                className="scroll-mt-28"
              >
                <CustomInstructionsEditor
                  {...editorHandlers.custom_instructions}
                  enabledTools={enabledTools}
                  dialogs={dialogs}
                  onNavigateToLocalTools={onNavigateToLocalTools}
                />
              </section>
            </TabsContent>

            <TabsContent
              value="channel-specific"
              forceMount
              hidden={activeTab !== 'channel-specific'}
              className="mt-6 px-4"
            >
              <ChannelGuidelinesEditor
                brainId={brainId}
                onEditorReady={handleChannelGuidelinesReady}
                onChange={handleChannelGuidelinesChange}
                onChannelChange={handleChannelChange}
                registerSectionRef={registerSectionRef}
                enabledTools={enabledTools}
                dialogs={dialogs}
                onNavigateToLocalTools={onNavigateToLocalTools}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right column: GuidelinesHeader rail */}
        {/* 34px = the tabs row's 24px + (36px h-9 trigger − 16px leading-4) / 2,
            centring the rail's smaller first row on it (within a pixel) */}
        <aside className="sticky top-0 hidden self-start pt-8.5 xl:block">
          <div className="mr-6 flex justify-center">
            <GuidelinesHeader className="flex-col justify-start gap-5" />
          </div>
        </aside>

        {!isHeaderVisible && (
          <div className="sticky bottom-4 z-10 col-span-full mx-auto w-fit self-end xl:col-span-1 xl:col-start-2">
            <UnsavedChangesAlert
              onSave={handleSave}
              saveStatus={patchBrainGuidelinesStatus}
              hasUnsavedChanges={hasUnsavedChanges}
              remainingCharacters={remainingCharacters}
            />
          </div>
        )}
      </div>

      <GuidelinesCharacterLimitModal
        open={isCharacterLimitModalOpen}
        onContinueEditing={handleContinueEditing}
        onDiscard={handleDiscardAndLeave}
      />
    </>
  );
};

// The editors initialise once per instance, so remounting is the only clean way to
// switch agents. `brainId`, not `brain?.brain_id` — the router sets it first.
export const GuidelinesPage = () => {
  useSegments(PageName.AI_AGENT_GUIDELINES);
  const brainId = useSelector(selectBrainId);

  // The rename sidebar nulls `brainId` on close and AIAgentLayout restores it a
  // tick later; remounting on that blip would drop the user's unsaved edits.
  const lastBrainIdRef = useRef<string | null>(null);
  if (brainId) {
    lastBrainIdRef.current = brainId;
  }
  const stableBrainId = brainId ?? lastBrainIdRef.current;

  if (!stableBrainId) {
    return <ContentSkeleton />;
  }

  return <GuidelinesPageEnhanced key={stableBrainId} brainId={stableBrainId} />;
};
